import React from "react";
import { Select, Tag, Tooltip } from "antd";

/**
 * Classification par âge et descripteurs de contenu (CMP-03).
 *
 * Les colonnes existaient en base et l'API les renvoyait déjà, mais aucun
 * formulaire ne permettait de les saisir : les contenus publiés sont restés
 * sans classification, alors qu'App Store et Play Console l'exigent — et que
 * le futur profil enfant n'a rien à filtrer sans elle.
 *
 * L'échelle retenue est celle des classifications européennes et africaines
 * usuelles. Les descripteurs voyagent en une chaîne séparée par des virgules,
 * forme que la colonne attend.
 */

export const AGES = [
  { valeur: 0, libelle: "Tout public", couleur: "green" },
  { valeur: 6, libelle: "6 ans et plus", couleur: "lime" },
  { valeur: 10, libelle: "10 ans et plus", couleur: "gold" },
  { valeur: 12, libelle: "12 ans et plus", couleur: "orange" },
  { valeur: 16, libelle: "16 ans et plus", couleur: "volcano" },
  { valeur: 18, libelle: "18 ans et plus", couleur: "red" },
];

export const DESCRIPTEURS = [
  { valeur: "violence", libelle: "Violence" },
  { valeur: "langage", libelle: "Langage grossier" },
  { valeur: "sexe", libelle: "Contenu sexuel" },
  { valeur: "drogues", libelle: "Drogues ou alcool" },
  { valeur: "peur", libelle: "Scènes effrayantes" },
  { valeur: "discrimination", libelle: "Discrimination" },
];

/** Âge minimum conseillé. */
export const SelecteurAgeMinimum: React.FC<{
  value?: number;
  onChange?: (valeur: number | undefined) => void;
}> = ({ value, onChange }) => (
  <Select
    allowClear
    placeholder="Âge minimum conseillé"
    value={value}
    onChange={onChange}
    options={AGES.map((a) => ({
      value: a.valeur,
      label: a.libelle,
    }))}
  />
);

/**
 * Descripteurs, convertis entre la liste de l'interface et la chaîne stockée.
 *
 * La valeur vide est renvoyée en `undefined` et non en chaîne vide : le
 * service n'écrase une classification existante que si la requête porte le
 * champ, et une chaîne vide l'effacerait.
 */
export const SelecteurDescripteurs: React.FC<{
  value?: string;
  onChange?: (valeur: string | undefined) => void;
}> = ({ value, onChange }) => (
  <Select
    mode="multiple"
    allowClear
    placeholder="Motifs de la classification"
    value={value ? value.split(",").filter(Boolean) : []}
    onChange={(liste: string[]) =>
      onChange?.(liste.length > 0 ? liste.join(",") : undefined)
    }
    options={DESCRIPTEURS.map((d) => ({ value: d.valeur, label: d.libelle }))}
  />
);

/** Pastille de classification, pour les listes et la fiche. */
export const EtiquetteClassification: React.FC<{
  age?: number | null;
  descripteurs?: string | null;
}> = ({ age, descripteurs }) => {
  if (age === null || age === undefined) {
    // Dire « non classé » plutôt que rien : une absence de classification est
    // une information, et c'est elle qui bloque la fiche App Store.
    return <Tag color="default">Non classé</Tag>;
  }
  const niveau = AGES.find((a) => a.valeur === age);
  const motifs = (descripteurs || "")
    .split(",")
    .filter(Boolean)
    .map((d) => DESCRIPTEURS.find((x) => x.valeur === d)?.libelle || d);

  const pastille = (
    <Tag color={niveau?.couleur || "default"}>
      {age === 0 ? "Tout public" : `${age}+`}
    </Tag>
  );

  return motifs.length > 0 ? (
    <Tooltip title={motifs.join(", ")}>{pastille}</Tooltip>
  ) : (
    pastille
  );
};
